import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LENGTH } from '@shared/config/allowedKeys.config';
import { ToasterService } from '@app/shared/services/toaster.service';
import { location, VendorInvite, FormErrorKey, FormErrorMessage} from '@app/pages/ops-vendor-management/invite-form/invite-form.config';
import { noOnlyZerosValidator } from '@utils/validators';

@Component({
  selector: 'app-invite-form',
  templateUrl: './invite-form.component.html',
  styleUrls: ['./invite-form.component.scss'],
})
export class InviteFormComponent implements OnInit {
  inviteForm!: FormGroup;
  selectedFiles: {
    name: string;
    size: number;
    formattedSize: string;
    file: File;
  }[] = [];
  fileError: string | null = null;
  maxLengthTen = LENGTH.MAX_LENGTH_TEN;
  locationOptions = location;
  VendorInvite = VendorInvite;

  @Output() close = new EventEmitter<void>();

  ngOnInit() {
    this.createForm();
  }
  createForm() {
    this.inviteForm = this.fb.group({
      organizationName: ['', Validators.required],
      organizationLocation: ['', Validators.required],
      organizationAddress: ['', Validators.required],
      pocName: ['', Validators.required],
      pocContact: ['', [Validators.required, noOnlyZerosValidator()]],
      pocDesignation: ['', Validators.required],
      pocEmail: ['', [Validators.required, Validators.email]],
    });
  }

  constructor(
    private fb: FormBuilder,
    private toasterService: ToasterService,
  ) {}

  isFieldValid(field: string): boolean {
    const control = this.inviteForm.get(field);
    return control ? control.valid || !control.touched : true;
  }

  getFieldError(field: string): string {
    const control = this.inviteForm.get(field);
    if (control && control.touched && control.invalid) {
      if (control.errors?.[FormErrorKey.Required]) return FormErrorMessage.Required;
      if (control.errors?.[FormErrorKey.Email]) return FormErrorMessage.Email;
      if (control.errors?.[FormErrorKey.OnlyZeros]) return FormErrorMessage.OnlyZeros;
    }
    return '';
  }

  validateContactNumber(event: Event) {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/\D/g, '');
    this.inviteForm.get('pocContact')?.setValue(input.value);
  }

  onBrowseClick(event: Event) {
    const fileInput = document.getElementById(
      'file-upload',
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const newFiles = Array.from(input.files).map((file) => ({
        name: file.name,
        size: file.size,
        formattedSize: this.getFormattedFileSize(file.size),
        file,
      }));
      const allFiles = [...this.selectedFiles, ...newFiles];
      const totalSize = allFiles.reduce(
        (sum, fileObj) => sum + fileObj.size,
        0,
      );

      if (totalSize > 50 * 1024 * 1024) {
        this.fileError = FormErrorMessage.FileSize;
        return;
      }

      this.selectedFiles = allFiles;
      this.fileError = null;
    }
  }

  getFormattedFileSize(size: number): string {
    if (size > 1024 * 1024) {
      return Math.floor(size / (1024 * 1024)) + ' MB';
    } else if (size > 1024) {
      return Math.floor(size / 1024) + ' KB';
    }
    return size + ' B';
  }

  removeFile(index: number) {
    this.selectedFiles.splice(index, 1);
  }

  submit() {
    if (this.inviteForm.invalid) {
      const errorMsg = FormErrorMessage.InvalidForm;
      if (this.toasterService) {
        this.toasterService.showError(errorMsg, VendorInvite.Error);
      } else {
        alert(errorMsg);
      }
      this.inviteForm.markAllAsTouched();
      return;
    }

    if (this.selectedFiles.length === 0) {
      this.fileError = FormErrorMessage.NoFile;
      return;
    }

    if (this.toasterService) {
      this.toasterService.showSuccess(FormErrorMessage.Success, VendorInvite.Success);
    } else {
      alert(FormErrorMessage.Success);
    }

    this.inviteForm.reset();
    this.selectedFiles = [];
    this.fileError = null;
    this.close.emit();
  }

  onCancel() {
    this.inviteForm.reset();
    this.selectedFiles = [];
    this.fileError = null;
    this.close.emit();
  }
}
